import type { BmBadgeVariant } from '../components/ui/BmBadge';
import {
  formatComparisonValue,
  formatPercentChip,
  isNegligibleComparisonBase,
} from './metricValueFormat';
import { formatPreviousPeriodLabel } from './previousPeriodLabel';
import { getSnapshotStatCardFootnote, isSnapshotStatCard } from './statCardDisplay';

/** Whether an increase vs previous period is good for the business. */
export type DeltaDirection = 'higher-is-better' | 'lower-is-better';

/** Metrics where delta direction has no clear business meaning — chip stays neutral. */
const NEUTRAL_DELTA_METRICS = new Set<string>(['avg-days-sale-to-cyd']);

const METRIC_DELTA_DIRECTION: Record<string, DeltaDirection> = {
  'products-sold': 'higher-is-better',
  'total-orders': 'higher-is-better',
  'total-sales': 'higher-is-better',
  'total-customers': 'higher-is-better',
  'new-customers': 'higher-is-better',
  'recurring-customers': 'higher-is-better',
  'retention-rate': 'higher-is-better',

  'total-cyd-units': 'lower-is-better',
  'orders-with-cyd': 'lower-is-better',
  'cyd-rate': 'lower-is-better',
  'total-cyd-amount': 'lower-is-better',
  'cyd-rate-amount': 'lower-is-better',

  'avg-resolution-time': 'lower-is-better',

  'open-orders': 'lower-is-better',
  'total-shipping-cost': 'lower-is-better',
  'average-shipping-cost': 'lower-is-better',
  'average-shipping-cost-sucursal': 'lower-is-better',
  'average-shipping-cost-domicilio': 'lower-is-better',
  'total-shipping-collected': 'higher-is-better',
  'shipping-collection-gap': 'higher-is-better',
  'shipping-quoted-collection-gap': 'higher-is-better',
};

export function getMetricDeltaDirection(metricId: string): DeltaDirection {
  if (metricId.includes('cyd') && !(metricId in METRIC_DELTA_DIRECTION)) {
    return 'lower-is-better';
  }
  return METRIC_DELTA_DIRECTION[metricId] ?? 'higher-is-better';
}

export function getDeltaBadgeVariant(
  metricId: string,
  current: number,
  previous: number
): BmBadgeVariant {
  if (current === previous || NEUTRAL_DELTA_METRICS.has(metricId)) {
    return 'neutral';
  }

  const direction = getMetricDeltaDirection(metricId);
  const increased = current > previous;
  const isGoodForBusiness =
    direction === 'higher-is-better' ? increased : !increased;

  return isGoodForBusiness ? 'insight' : 'negative';
}

export interface PeriodComparisonDisplay {
  chipLabel: string | null;
  chipVariant: BmBadgeVariant | null;
  footnote: string;
}

export function buildPeriodComparison(
  metricId: string,
  current: number,
  previous: number,
  startDate?: string,
  endDate?: string
): PeriodComparisonDisplay {
  if (isSnapshotStatCard(metricId)) {
    return {
      chipLabel: null,
      chipVariant: null,
      footnote: getSnapshotStatCardFootnote(metricId) ?? '',
    };
  }

  const baseLabel = formatComparisonValue(metricId, previous);
  const periodLabel = formatPreviousPeriodLabel(startDate, endDate);
  const footnote = `vs. ${baseLabel} · ${periodLabel}`;

  if (current === previous) {
    return { chipLabel: null, chipVariant: null, footnote };
  }

  if (isNegligibleComparisonBase(metricId, previous)) {
    return { chipLabel: null, chipVariant: null, footnote };
  }

  const chipLabel = formatPercentChip(current, previous);
  if (!chipLabel) {
    return { chipLabel: null, chipVariant: null, footnote };
  }

  return {
    chipLabel,
    chipVariant: getDeltaBadgeVariant(metricId, current, previous),
    footnote,
  };
}
